/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2021 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2021 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */
import { Grid, MenuItem } from '@mui/material';
import type { TFunction } from 'i18next';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  isEmpty,
  isInteger,
  isValidMax,
  isValidMin,
  ReactHookFormSelect,
  ReactHookFormNumberField,
  useDraftFormHelpers,
} from '@opengeoweb/form-fields';
import type {
  ConfigurableFormFieldProps,
  FIRConfigAirmet,
  ProductConfig,
} from '../../../types';
import { WindUnit } from '../../../types';
import { useSigmetAirmetTranslation } from '../../../utils/i18n';
import { styles } from '../ProductForm.styles';
import {
  getActiveFIRArea,
  getAllowedUnits,
  getFieldLabel,
  getMaxWindSpeedValue,
  getMinWindSpeedValue,
  triggerValidations,
} from '../utils';

export const DEFAULT_ROUNDING_SURFACE_WIND_DIRECTION = 10;

export const invalidSurfaceWindDirectionStepsMessage = (
  t: TFunction,
  value = DEFAULT_ROUNDING_SURFACE_WIND_DIRECTION,
): string =>
  t('invalid-surface-wind-direction-message', {
    value,
  });

export const validateSurfaceWindDirection = (
  t: TFunction,
  value: string,
  productConfig: ProductConfig,
  selectedFIR: string,
): boolean | string => {
  if (isEmpty(value)) {
    return true;
  }

  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    wind_direction_rounding = DEFAULT_ROUNDING_SURFACE_WIND_DIRECTION,
  } = getActiveFIRArea(selectedFIR, productConfig) as FIRConfigAirmet;

  // Parse to integer to check for steps
  const intVisibility = parseInt(value, 10);
  return (
    intVisibility % wind_direction_rounding === 0 ||
    invalidSurfaceWindDirectionStepsMessage(t, wind_direction_rounding)
  );
};

const SurfaceWind: React.FC<ConfigurableFormFieldProps> = ({
  isDisabled,
  isReadOnly,
  onChange = (): void => null!,
  productConfig,
}: ConfigurableFormFieldProps) => {
  const { t } = useSigmetAirmetTranslation();
  const { trigger, getValues } = useFormContext();
  const { isRequired } = useDraftFormHelpers();

  const getFIRValue = (): string =>
    getValues('locationIndicatorATSR') as string;

  // Get allowed speed units based on selected FIR - if no FIR selected, allow all units
  const allowedWindUnitsForFir = getAllowedUnits(
    getFIRValue(),
    productConfig,
    'surfacewind_unit',
    WindUnit,
  );

  const windLabel = getFieldLabel(
    t,
    t('surface-wind-label'),
    isReadOnly!,
    t('set'),
  );
  const speedLabel = getFieldLabel(
    t,
    t('surface-wind-speed-label'),
    isReadOnly!,
    t('set'),
  );

  return (
    <Grid container spacing={2}>
      <Grid container justifyContent="flex-end" size={4} />
      <Grid container justifyContent="flex-start" size={8}>
        <Grid size={12} sx={styles.surfaceWind}>
          <ReactHookFormNumberField
            data-testid="surfaceWind-windDirection"
            disabled={isDisabled}
            helperText={t('surface-wind-helper-text')}
            isReadOnly={isReadOnly}
            label={windLabel}
            name="windDirection"
            rules={{
              min: {
                value: 10,
                message: t('surface-wind-rule-min-max-message'),
              },
              max: {
                value: 360,
                message: t('surface-wind-rule-min-max-message'),
              },
              validate: {
                isRequired,
                validateSurfaceWindDirection: (value): boolean | string =>
                  validateSurfaceWindDirection(
                    t,
                    value,
                    productConfig,
                    getFIRValue(),
                  ),
              },
            }}
            size="small"
          />
        </Grid>
        <Grid container size={12} spacing={0} sx={{ marginTop: -2 }}>
          <Grid size={5}>
            <ReactHookFormSelect
              data-testid="surfaceWind-windUnit"
              defaultValue={'KT' as WindUnit}
              disabled={isDisabled}
              isReadOnly={isReadOnly}
              label={t('unit-label')}
              name="windUnit"
              onChange={(): void => {
                triggerValidations(['windSpeed'], getValues, trigger);
                onChange();
              }}
              rules={{ validate: { isRequired } }}
              size="small"
              sx={styles.unit}
            >
              {Object.keys(allowedWindUnitsForFir).map((key) => (
                <MenuItem key={key} value={key}>
                  {allowedWindUnitsForFir[key]}
                </MenuItem>
              ))}
            </ReactHookFormSelect>
          </Grid>
          <Grid size={7}>
            <ReactHookFormNumberField
              data-testid="surfaceWind-windSpeed"
              disabled={isDisabled}
              isReadOnly={isReadOnly}
              label={speedLabel}
              name="windSpeed"
              rules={{
                validate: {
                  isRequired,
                  isInteger,
                  min: (value): boolean | string =>
                    // The min level depends on the unit
                    isValidMin(
                      value,
                      getMinWindSpeedValue(
                        getValues('windUnit'),
                        getFIRValue(),
                        productConfig,
                      ),
                    ) ||
                    t('surface-wind-minimum-wind-speed', {
                      windUnit:
                        WindUnit[
                          getValues('windUnit') as keyof typeof WindUnit
                        ],
                      minWindSpeedValue: getMinWindSpeedValue(
                        getValues('windUnit'),
                        getFIRValue(),
                        productConfig,
                      ),
                    }),

                  max: (value): boolean | string =>
                    // The max level depends on the unit
                    isValidMax(
                      value,
                      getMaxWindSpeedValue(
                        getValues('windUnit'),
                        getFIRValue(),
                        productConfig,
                      ),
                    ) ||
                    t('surface-wind-maximum-wind-speed', {
                      windUnit:
                        WindUnit[
                          getValues('windUnit') as keyof typeof WindUnit
                        ],
                      maxWindSpeedValue: getMaxWindSpeedValue(
                        getValues('windUnit'),
                        getFIRValue(),
                        productConfig,
                      ),
                    }),
                },
              }}
              size="small"
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SurfaceWind;
